'use client';
import { ReactNode, useState } from 'react';
import TabHeaders from './_TabHeaders';
import TabContent from './_TabContent';

export interface TabProps {
  label: string;
  onClick?: (index?: number) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  href?: string;
}

export interface TabItem extends TabProps {
  content: ReactNode;
}

export interface TabWrapperProps {
  tabs: TabItem[];
  initialTab?: number;
  tabLabel?: string;
  className?: string;
  tabHeadersClassName?: string;
  tabContentClassName?: string;
}

export const Tabs = ({
  tabs,
  initialTab = 0,
  tabLabel = '',
  className = '',
  tabHeadersClassName = '',
  tabContentClassName = ''
}: TabWrapperProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialTab);

  const handleTabChange = (index: number) => {
    setCurrentIndex(index);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    let newIndex = index;
    
    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        newIndex = Math.min(index + 1, tabs.length - 1);
        handleTabChange(newIndex);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = Math.max(index - 1, 0);
        handleTabChange(newIndex);
        break;
      case 'Tab':
        break;
      default:
        break;
    }
  };

  const handleTabFocus = (index: number) => {
    if (index !== currentIndex) {
      handleTabChange(index);
    }
  };

  const tabsWithHandlers = tabs.map((tab, index) => ({
    ...tab,
    onClick: tab.onClick || (() => handleTabChange(index)),
    onKeyDown: (e: React.KeyboardEvent) => handleKeyDown(e, index),
    onFocus: () => handleTabFocus(index)
  }));

  return (
    <div className={className}>
      <TabHeaders
        tabs={tabsWithHandlers}
        currentIndex={currentIndex}
        tabLabel={tabLabel}
        className={tabHeadersClassName}
      />
      
      {tabs.map((tab, index) => (
        <TabContent
          key={`tabcontent-${index}`}
          id={`tabpanel-${index}`}
          labelledBy={`tab-${index}`}
          active={currentIndex === index}
          className={tabContentClassName}
        >
          {tab.content}
        </TabContent>
      ))}
    </div>
  );
};

export default Tabs;